import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ROUTE_REQUIRED_FILTERS,
  CONTEXT_KEYS,
  saveUserFilters,
  getFiltersForRoute,
  getCacheKey,
} from '../config/routeFilters';

export function useRouteFilters(searchParams, setSearchParams) {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, '') || '/';
  const isInitialMount = useRef(true);
  const previousCacheKey = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const basePath = '/' + pathname.split('/')[1];

    if (!(basePath in ROUTE_REQUIRED_FILTERS)) {
      isInitialMount.current = false;
      previousCacheKey.current = null;
      setIsReady(true);
      return;
    }

    const currentCacheKey = getCacheKey(basePath, searchParams);

    const shouldRestoreFilters =
      isInitialMount.current ||
      (previousCacheKey.current !== null && previousCacheKey.current !== currentCacheKey);

    if (shouldRestoreFilters) {
      isInitialMount.current = false;

      const filtersToApply = getFiltersForRoute(basePath, searchParams);
      const requiredFilters = ROUTE_REQUIRED_FILTERS[basePath] || {};

      let needsUpdate = false;
      const newParams = new URLSearchParams();

      for (const contextKey of CONTEXT_KEYS) {
        const value = searchParams.get(contextKey);
        if (value) {
          newParams.set(contextKey, value);
        }
      }

      for (const [key, value] of Object.entries(requiredFilters)) {
        newParams.set(key, value);
        if (searchParams.get(key) !== value) {
          needsUpdate = true;
        }
      }

      for (const [key, value] of Object.entries(filtersToApply)) {
        if (!Object.keys(requiredFilters).includes(key)) {
          newParams.set(key, value);
          if (searchParams.get(key) !== value) {
            needsUpdate = true;
          }
        }
      }

      if (needsUpdate) {
        setSearchParams(newParams, { replace: true });
      } else {
        setIsReady(true);
      }

      previousCacheKey.current = getCacheKey(basePath, newParams);
    } else {
      previousCacheKey.current = currentCacheKey;
      saveUserFilters(basePath, searchParams);
      setIsReady(true);
    }
  }, [pathname, searchParams, setSearchParams]);

  return { pathname, isReady };
}
